import { FC, useState, useEffect, useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { FaChevronLeft, FaChevronRight, FaFilter, FaSyncAlt } from 'react-icons/fa';

import { buildClientEntity } from 'utils/helpers/client';
import { useActiveClientContext } from 'store/active-client';

import { useQuery, useQueryClient } from 'react-query';

import qs from 'query-string';
import API from 'core/Api';
import { ISelectOptionProps } from 'components/form/types';
import Datepicker from 'components/form/Datepicker';
import MultiSelectFilter from 'components/table/MultiSelectFilter';
import Button from 'components/Button';
import { useLanguageContext } from 'store/language';
import { useTranslation } from 'react-i18next';
import format from 'date-fns/format';
import { formatDate } from 'utils/helpers/formatters';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import enGB from 'date-fns/locale/en-GB';
import bg from 'date-fns/locale/bg';
import styled from 'styled-components';
import { getCalendarLegendItems } from 'utils/helpers/leaves';
import TooltipWrapper from 'components/TooltipWrapper';
import { ILeaveData, IWorkTripData } from 'components/leavescalendargrid';
import { getSubdomain } from 'utils/helpers/routing';
import { getUserInfo } from 'utils/auth';
import CustomEventWrapper from '../components/CustomEventWrapper';
import LeavesGrid from '../components/LeavesGrid';
import InnerWrapper from '../InnerWrapper';

import 'react-big-calendar/lib/css/react-big-calendar.css';

const Screen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const [expandEvents, setExpandEvents] = useState<boolean>(false);
  const [showGridView, setShowGridView] = useState<boolean>(false);
  const [showWorkTrips, setShowWorkTrips] = useState<boolean>(true);

  const isOperator = getSubdomain() === 'operators';
  const userInfo = getUserInfo();
  const isLeavesModuleActive = isOperator ? client?.leaves : userInfo.featureLeavesIsActive;
  const isWorkTripsModuleActive = isOperator ? client?.is_work_trips : userInfo.featureWorkTrips;
  const isFeatureShowWFHLeaveTypeInMainDropdownActive = userInfo.featureShowWFHLeaveTypeInMainDropdown;

  const messages = {
    allDay: t('leaves.employer.calendar.allDay'),
    previous: t('leaves.employer.calendar.previous'),
    next: t('leaves.employer.calendar.next'),
    today: t('leaves.employer.calendar.today'),
    month: t('leaves.employer.calendar.month'),
    week: t('leaves.employer.calendar.week'),
    day: t('leaves.employer.calendar.day'),
    agenda: t('leaves.employer.calendar.agenda'),
    date: t('leaves.employer.calendar.date'),
    time: t('leaves.employer.calendar.time'),
    event: t('leaves.employer.calendar.event'),
    showMore: (total: number) => t('leaves.employer.calendar.showMore', { total }),
  };
  const locales = {
    bg,
    en: enGB,
  };

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
  });

  const calculateRangeDates = () => {
    const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
    const startDayPosition = startDate.getDay();
    const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const endtDayPosition = endDate.getDay();

    startDate.setDate(startDate.getDate() - (startDayPosition - 1));
    endDate.setDate(endDate.getDate() + (7 - endtDayPosition));

    return {
      startDate: formatDate(startDate, 'YYY-mm-dd'),
      endDate: formatDate(endDate, 'YYY-mm-dd'),
    };
  };

  const [filters, setFilters] = useState<any>({
    status: ['pending', 'confirmed', 'approved'],
    user: null,
    group: null,
    type: null,
  });

  const [date, setDate] = useState<Date>(new Date());
  const year = date.getFullYear();

  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { startDate, endDate } = calculateRangeDates();

  // This will update the state, when month is changed through the Calendar component (not the Datepicker),
  // and both components will have the same synchronized dates and GET request will be made for the selected period.
  const [calendarMonth, setCalendarMonth] = useState<number>(0);

  const {
    isLoading: isLoadingLeavesCalendarData,
    error,
    data: calendarEventsData = [],
  } = useQuery<ILeaveData[], Error>(
    [buildClientEntity('leaves', client), { startDate, endDate, ...filters }],
    (): Promise<ILeaveData[]> => {
      const queryString = qs.stringify(filters, {
        arrayFormat: 'bracket',
        skipNull: true,
      });
      return API.get(
        `leaves/calendar?limit=1000&offset=0&date_from=${startDate}&date_to=${endDate}&${queryString}`
      ).then(res => {
        return (
          res.data?.items
            ?.map((leave: any) => ({
              ...leave,
              type:
                isFeatureShowWFHLeaveTypeInMainDropdownActive && leave.type === 'additional'
                  ? leave.sub_type
                  : leave.type,
              start: new Date(leave.date_from).setHours(0, 0, 0, 0),
              end: new Date(leave.date_to).setHours(23, 59, 59, 999),
            }))
            // @ts-ignore
            .sort(prev => {
              // Push leaves with pending status on top
              if (prev.status === 'pending') return -1;
              return 0;
            })
        );
      });
    },
    {
      enabled: !!isLeavesModuleActive,
    }
  );

  const {
    isLoading: isLoadingWorkTripsCalendarData,
    error: errorWorkTripsCalendarData,
    data: workTripsCalendarData = [],
  } = useQuery<IWorkTripData[], Error>(
    [buildClientEntity('work-trips-calendar', client), { startDate, endDate, ...filters }],
    (): Promise<IWorkTripData[]> => {
      const queryString = qs.stringify(
        { user: filters.user, group: filters.group },
        {
          arrayFormat: 'bracket',
          skipNull: true,
        }
      );
      return API.get(
        `work-trips/calendar?limit=1000&offset=0&date_from=${startDate}&date_to=${endDate}&${queryString}`
      ).then(res => {
        return res.data?.items?.map((trip: any) => ({
          ...trip,
          start: new Date(trip.from_date).setHours(0, 0, 0, 0),
          end: new Date(trip.to_date).setHours(23, 59, 59, 999),
        }));
      });
    },
    {
      enabled: !!isWorkTripsModuleActive,
    }
  );

  const {
    isLoading: isLoadingHolidays,
    error: errorHolidays,
    data: holidaysData = [],
  } = useQuery<ILeaveData[], Error>([buildClientEntity('holidays', client), year], (): Promise<ILeaveData[]> => {
    return API.get(`holidays/${year}`).then(res => {
      return res.data?.items?.map((holiday: any, index: number) => {
        // Set the start time to 00:00:00
        const start = new Date(holiday.date);
        start.setHours(0, 0, 0, 0);

        // Set the end time to 23:59:59
        const end = new Date(holiday.date);
        end.setHours(23, 59, 59, 999);

        return {
          ...holiday,
          id: index,
          title: holiday.name,
          start,
          end,
          type: 'holiday',
          allDay: true,
        };
      });
    });
  });

  const {
    isFetching: isFetchingLeavesDropdowns,
    isLoading: isLoadingLeavesDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('leaves-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get('leaves/dropdown-options').then(res => res.data && res.data[language || 'bg']);
    },
    {
      cacheTime: 0,
      enabled: !!isLeavesModuleActive,
    }
  );

  const calendarLegendItems = useMemo(
    () => getCalendarLegendItems(isFeatureShowWFHLeaveTypeInMainDropdownActive),
    [isFeatureShowWFHLeaveTypeInMainDropdownActive]
  );

  const updateFilter = (key: string, selected: ISelectOptionProps[]) =>
    selected
      ? // @ts-ignore
        setFilters(fltrs => ({
          ...fltrs,
          [key]: selected.map(opt => opt.value),
        }))
      : // @ts-ignore
        setFilters(fltrs => ({
          ...fltrs,
          [key]: null,
        }));
  const CustomToolbar = (toolbar: any) => {
    const goToBack = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() - 1);
      toolbar.onNavigate('prev');
      setCalendarMonth(toolbar.date.getMonth());
      setDate(new Date(toolbar.date));
    };

    const goToNext = () => {
      toolbar.date.setMonth(toolbar.date.getMonth() + 1);
      toolbar.onNavigate('next');
      setCalendarMonth(toolbar.date.getMonth());
      setDate(new Date(toolbar.date));
    };
    return (
      <StyledCustomToolbar>
        <button
          type="button"
          onClick={goToBack}
        >
          <FaChevronLeft size={16} />
        </button>

        <h5 className="text-center">
          {t(`leaves.months.${date.getMonth() + 1}`)} {date.getFullYear()}
        </h5>

        <button
          type="button"
          onClick={goToNext}
        >
          <FaChevronRight size={16} />
        </button>
      </StyledCustomToolbar>
    );
  };

  const allCalendarEvents = [
    ...holidaysData,
    ...calendarEventsData,
    ...(showWorkTrips ? workTripsCalendarData : []),
  ].map(event => ({
    ...event,
    start: new Date(event.start),
    end: new Date(event.end),
  }));

  const queryClient = useQueryClient();

  return (
    <InnerWrapper
      isLoading={
        isFetchingLeavesDropdowns || isLoadingLeavesCalendarData || isLoadingHolidays || isLoadingWorkTripsCalendarData
      }
      showViewToggle
      isGridView={showGridView}
      onToggleView={() => setShowGridView(prev => !prev)}
      expandEvents={expandEvents}
      onToggleExpand={() => setExpandEvents(prev => !prev)}
      isWorkTripsModuleActive={isWorkTripsModuleActive}
      showWorkTrips={showWorkTrips}
      toggleWorkTrips={() => setShowWorkTrips(prev => !prev)}
    >
      <MobileNav>
        <RefreshButton
          className="refresh-button"
          onClick={() => queryClient.resetQueries('leaves')}
        >
          <FaSyncAlt style={{ fontSize: '16px' }} />
        </RefreshButton>
        <FilterButton>
          <TooltipWrapper
            id="tooltip-add-btn"
            overlay={t('documents.table.filtersBtn')}
            wrapperClass="d-block col-xs-12 col-md-auto mb-0"
          >
            <Button
              ariaLabel="Filter button"
              onClick={() => setIsFiltersOpen(prevIsFiltersOpen => !prevIsFiltersOpen)}
              className="btn btn-secondary btn-sm"
            >
              <FaFilter />
            </Button>
          </TooltipWrapper>
        </FilterButton>
      </MobileNav>
      {(isFiltersOpen || !isMobile) && (
        <CalendarFiltersWrapper className="d-flex flex-column-reverse flex-lg-row flex-lg-nowrap align-items-start mb-1 mb-lg-4">
          <div className="pt-3 pt-lg-0 flex-lg-grow-1 legend-col">
            <TooltipWrapper
              id="tooltip-legend"
              overlay={`${t('leaves.legend.tooltip')}`}
              wrapperClass="d-lg-block legend-tooltip-wrapper"
            >
              <CalendarLegend>
                {calendarLegendItems.map(item => (
                  <LegendItem
                    style={{
                      backgroundColor: `${item.backgroundColor}`,
                    }}
                  >
                    {t(`leaves.legend.${item.type}`)}
                  </LegendItem>
                ))}
              </CalendarLegend>
            </TooltipWrapper>
          </div>
          <div className="d-flex flex-wrap flex-lg-nowrap flex-lg-shrink-0 justify-content-end align-items-center align-items-lg-start flex-lg-row">
            <Datepicker
              wrapperClassName="c-custom-select c-filter c-filter--small c-filter--date-width ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
              className="form-control"
              placeholder={t('leaves.employer.calendar.date')}
              onChange={(d: Date) => {
                setDate(d || new Date());
              }}
              selected={date}
              dateFormat="MM/YYY"
              showMonthYearPicker
            />
            <MultiSelectFilter
              className="c-custom-select c-filter c-filter--medium ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
              // @ts-ignore
              value={dropdowns?.status.filter(option => filters?.status?.includes(option.value))}
              placeholder={t('table.statusFilter')}
              options={dropdowns?.status}
              onChange={selected => updateFilter('status', selected)}
              isClearable
              showTotal
              allowSelectAll
            />
            <MultiSelectFilter
              className="c-custom-select c-filter ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
              // @ts-ignore
              value={dropdowns?.user.filter(option => filters?.user?.includes(option.value))}
              placeholder={t('documents.table.employee')}
              options={dropdowns?.user}
              onChange={selected => updateFilter('user', selected)}
              isClearable
              showTotal
              limitOptions
              allowSelectAll
            />
            <MultiSelectFilter
              className="c-custom-select c-filter ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
              // @ts-ignore
              value={dropdowns?.group.filter(option => filters?.group?.includes(option.value))}
              placeholder={t('documents.table.group')}
              options={dropdowns?.group}
              onChange={selected => updateFilter('group', selected)}
              isClearable
              showTotal
              allowSelectAll
            />
            <MultiSelectFilter
              className="c-custom-select c-filter ml-0 ml-md-2 mr-0 mr-md-2 mb-3 mb-lg-0"
              // @ts-ignore
              value={dropdowns?.type.filter(option => filters?.type?.includes(option.value))}
              placeholder={t('table.typeFilter')}
              options={dropdowns?.type}
              onChange={selected => updateFilter('type', selected)}
              isClearable
              showTotal
              allowSelectAll
            />
          </div>
        </CalendarFiltersWrapper>
      )}
      {!showGridView && (
        <CustomExpandCalendar>
          <Calendar
            className="calendar-expanded"
            date={date}
            showAllEvents={expandEvents}
            events={allCalendarEvents as any}
            localizer={localizer}
            culture={language || 'bg'}
            views={{
              month: true,
            }}
            components={{
              eventWrapper: CustomEventWrapper,
              toolbar: CustomToolbar,
            }}
            toolbar
            messages={messages}
            popup
            style={{ height: 'auto' }}
            // @ts-ignore
            dayPropGetter={(d: Date) => {
              const month = d.getMonth() + 1;
              const day = d.getDate();
              const formattedDate = `${year}-${month >= 10 ? month : `0${month}`}-${day >= 10 ? day : `0${day}`}`;
              const holidaysDatesArr: string[] = [];
              // eslint-disable-next-line no-plusplus
              holidaysData.forEach(j => holidaysDatesArr.push(j.date));
              if (holidaysDatesArr.includes(formattedDate)) {
                return {
                  style: {
                    backgroundColor: '#fc925e',
                  },
                };
              }
            }}
          />
        </CustomExpandCalendar>
      )}
      {showGridView && (
        <LeavesGrid
          leavesData={calendarEventsData}
          holidaysData={holidaysData}
          workTripsData={showWorkTrips ? workTripsCalendarData : []}
          date={date}
          onDateChange={setDate}
        />
      )}
    </InnerWrapper>
  );
};

export default Screen;

const CustomExpandCalendar = styled.div`
  .rbc-month-row {
    display: inline-table !important;
    flex: 0 0 0 !important;
    min-height: 120px !important;
  }
`;

const CalendarFiltersWrapper = styled.div`
  .legend-col {
    @media (max-width: 992px) {
      margin-bottom: 24px;
    }

    @media (min-width: 992px) {
      min-width: 0;
    }
  }

  .legend-tooltip-wrapper {
    @media (min-width: 992px) {
      width: 100%;
    }
  }

  .c-filter {
    max-width: 210px;
    width: 100%;
  }

  .c-filter--small {
    max-width: 80px;
  }

  .c-filter--medium {
    max-width: 180px;
    min-width: 180px;
  }

  @media (max-width: 992px) {
    .c-filter {
      max-width: 100%;
      width: 50%;
    }

    > *:last-child {
      margin-bottom: 24px;
    }
  }

  @media (max-width: 768px) {
    .c-filter {
      width: 100%;
    }
  }
`;

const StyledCustomToolbar = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 16px;

  h5 {
    margin: 0;
    min-width: 360px;
  }

  button {
    border: 0;
    background: none;
    opacity: 0.4;
    transition: 0.3s;

    svg {
      fill: #575962;
    }

    &:hover {
      opacity: 1;
      transition: 0.3s;
    }
  }
`;

const CalendarLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  color: white;
  align-items: center;
  justify-content: flex-start;

  p {
    margin: 0 0 4px 0;
    padding: 0 10px;
  }

  @media (max-width: 992px) {
    justify-content: center;
  }

  @media (min-width: 1660px) {
    flex-wrap: nowrap;
  }
`;

const LegendItem = styled.div`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`;

const FilterButton = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: block;
    margin-bottom: 0.5rem;
  }
`;

const RefreshButton = styled.div`
  display: none;

  &:hover {
    color: #32bfa3;
  }

  @media (max-width: 768px) {
    height: 30px;
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    margin-right: -0.5rem;
    z-index: 2;
  }
`;

const MobileNav = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    margin-right: -15px;
  }
`;
